// ContactUs.jsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next'; // <-- Important
import { motion } from 'framer-motion';
import { FaInstagram, FaWhatsapp, FaEnvelope } from 'react-icons/fa';
import contactUsBg from '../assets/Japan/iloveimg-converted (1)/IMG_2813.jpg';

// Motion variants
const sectionVariants = {
    hidden: { opacity: 0, y: 60 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.9, ease: "easeOut" } },
};

const ContactUs = () => {
    const { t } = useTranslation('contactus'); // Use contactus namespace

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        contactNumber: '',
        message: '',
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSuccessMessage('');
        setErrorMessage('');

        const phoneNumber = '819082514053'; // Japan WhatsApp number
        const text = `Sonic Tours Inquiry
Name: ${formData.name}
Email: ${formData.email}
Contact Number: ${formData.contactNumber}
Message: ${formData.message}`;
        const formattedMessage = encodeURIComponent(text);
        const whatsappUrl = `https://wa.me/${phoneNumber}?text=${formattedMessage}`;

        try {
            window.open(whatsappUrl, '_blank');
            setTimeout(() => {
                setIsSubmitting(false);
                setSuccessMessage(t('successMsg'));
                setFormData({ name: '', email: '', contactNumber: '', message: '' });
            }, 1000);
        } catch (error) {
            setIsSubmitting(false);
            setErrorMessage(t('errorMsg'));
        }
    };

    return (
        <div className="bg-[#07111f] min-h-screen text-white">
            {/* HERO SECTION */}
            <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="relative flex h-[60vh] w-full items-center justify-center font-lato md:h-[50vh] lg:h-[80vh] overflow-hidden"
            >
                <img
                    src={contactUsBg}
                    alt="Contact Aamy Tours Sri Lanka"
                    className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#07111f]/90 via-[#07111f]/75 to-[#07111f]/40" />

                <div className="relative z-10 flex flex-col items-center justify-center pt-[80px] px-4 sm:px-10 lg:px-16">

                    <h1 className="text-center font-serif text-4xl font-semibold leading-tight sm:text-5xl lg:text-6xl">
                        <span className="block text-[#7fd3ff]">{t('heroTitle')}</span>
                    </h1>
                    <p className="mt-6 max-w-2xl text-center text-lg leading-8 text-white/90 sm:text-xl">
                        {t('heroSubtitle')}
                    </p>

                    {/* Decorative SVGs remain unchanged */}
                    <div className="absolute left-6 top-12 md:left-10 md:top-16">
                        <svg width="320" height="280" viewBox="0 0 320 280" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M320 1H1V279.5" stroke="url(#gradient1)" strokeWidth="1.5" />
                            <defs>
                                <linearGradient id="gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#0074D9" />
                                    <stop offset="100%" stopColor="#7FDBFF" />
                                </linearGradient>
                            </defs>
                        </svg>
                    </div>
                    <div className="absolute -bottom-12 right-6 md:-bottom-16 md:right-10">
                        <svg width="320" height="280" viewBox="0 0 320 280" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M0 279.5H319V0" stroke="url(#gradient2)" strokeWidth="1.5" />
                            <defs>
                                <linearGradient id="gradient2" x1="0%" y1="0%" x2="100%" y2="100%">
                                    <stop offset="0%" stopColor="#7FDBFF" />
                                    <stop offset="100%" stopColor="#0074D9" />
                                </linearGradient>
                            </defs>
                        </svg>
                    </div>
                </div>
            </motion.div>

            {/* CONTACT INFO + FORM */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="mx-auto container px-6 py-20 sm:px-10 lg:px-16"
            >
                <div className="">
                    <div className="flex flex-col lg:flex-row items-start gap-12 lg:gap-20">
                        {/* Left: Contact Info */}
                        <div className="w-full lg:w-1/2 space-y-10">
                            <div className="max-w-3xl">
                                <p className="text-sm font-semibold uppercase tracking-[0.35em] text-[#f7d774]">
                                    {t('feelFree')}
                                </p>
                                <h1 className="mt-4 font-serif text-3xl font-semibold text-white sm:text-4xl">
                                    {t('planDream')}
                                </h1>
                                <p className="mt-5 text-lg leading-8 text-white/80">
                                    {t('reachOutDesc')}
                                </p>
                            </div>

                            <div className="space-y-8 mt-12">
                                <div className="rounded-[1.25rem] border border-[#7fd3ff]/20 bg-white/5 p-6">
                                    <h3 className="text-xl font-semibold text-[#7fd3ff]">
                                        {t('callCenter')}
                                    </h3>
                                    <p className="text-base leading-7 text-white/80 mt-3">{t('phone')}</p>
                                </div>

                                <div className="rounded-[1.25rem] border border-[#7fd3ff]/20 bg-white/5 p-6">
                                    <h3 className="text-xl font-semibold text-[#7fd3ff]">
                                        {t('emailTitle')}
                                    </h3>
                                    <p className="text-base leading-7 text-white/80 mt-3">{t('email')}</p>
                                </div>

                                <div className="rounded-[1.25rem] border border-[#7fd3ff]/20 bg-white/5 p-6">
                                    <h3 className="text-xl font-semibold text-[#7fd3ff]">
                                        {t('locationTitle')}
                                    </h3>
                                    <p className="text-base leading-7 text-white/80 mt-3">
                                        {t('location')}
                                    </p>
                                </div>

                                <div className="rounded-[1.25rem] border border-[#7fd3ff]/20 bg-white/5 p-6">
                                    <h3 className="text-xl font-semibold text-[#7fd3ff]">
                                        {t('socialTitle')}
                                    </h3>
                                    <div className="flex space-x-5 mt-4">
                                        <a href="https://www.instagram.com/sonictours.japan?igsh=MTgzYWtka2lnNzV5ag==" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
                                            <FaInstagram className="h-8 w-8 text-[#7fd3ff] hover:text-[#f7d774] transition-all duration-300 hover:scale-110" />
                                        </a>
                                        <a href="https://wa.me/+819082514053" target="_blank" rel="noopener noreferrer" aria-label="WhatsApp">
                                            <FaWhatsapp className="h-8 w-8 text-[#7fd3ff] hover:text-[#f7d774] transition-all duration-300 hover:scale-110" />
                                        </a>
                                        <a href="mailto:vbuddika@sonictourssl.com" aria-label="Email">
                                            <FaEnvelope className="h-8 w-8 text-[#7fd3ff] hover:text-[#f7d774] transition-all duration-300 hover:scale-110" />
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right: Contact Form */}
                        <div className="w-full lg:w-1/2">
                            <motion.div
                                initial={{ opacity: 0, x: 60 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.9, delay: 0.3 }}
                                className="overflow-hidden rounded-[1.5rem] border border-white/10 bg-[#0f1d33] shadow-[0_20px_50px_rgba(0,0,0,0.25)] p-8 md:p-12"
                            >
                                <h2 className="text-[#7fd3ff] text-2xl md:text-3xl font-semibold">
                                    {t('getInTouch')}
                                </h2>
                                <p className="text-base leading-7 text-white/80 mt-3">
                                    {t('getInTouchDesc')}
                                </p>

                                <form className="mt-10 space-y-7" onSubmit={handleSubmit}>
                                    <div>
                                        <label className="block text-white text-sm font-semibold">
                                            {t('nameLabel')}
                                        </label>
                                        <input
                                            type="text"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="w-full mt-2 border-b-2 border-white/30 bg-transparent py-3 text-white text-base outline-none focus:border-[#7fd3ff] transition-all duration-300 placeholder-white/40"
                                            placeholder={t('namePlaceholder')}
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-white text-sm font-semibold">
                                            {t('emailLabel')}
                                        </label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleChange}
                                            className="w-full mt-2 border-b-2 border-white/30 bg-transparent py-3 text-white text-base outline-none focus:border-[#7fd3ff] transition-all duration-300 placeholder-white/40"
                                            placeholder={t('emailPlaceholder')}
                                            required
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-white text-sm font-semibold">
                                            {t('phoneLabel')}
                                        </label>
                                        <input
                                            type="tel"
                                            name="contactNumber"
                                            value={formData.contactNumber}
                                            onChange={handleChange}
                                            className="w-full mt-2 border-b-2 border-white/30 bg-transparent py-3 text-white text-base outline-none focus:border-[#7fd3ff] transition-all duration-300 placeholder-white/40"
                                            placeholder={t('phonePlaceholder')}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-white text-sm font-semibold">
                                            {t('messageLabel')}
                                        </label>
                                        <textarea
                                            name="message"
                                            value={formData.message}
                                            onChange={handleChange}
                                            className="w-full mt-2 border-b-2 border-white/30 bg-transparent py-3 text-white text-base outline-none focus:border-[#7fd3ff] transition-all duration-300 resize-none placeholder-white/40"
                                            rows={5}
                                            placeholder={t('messagePlaceholder')}
                                            required
                                        />
                                    </div>

                                    {successMessage && (
                                        <p className="text-[#7fd3ff] text-sm font-medium animate-pulse">
                                            {successMessage}
                                        </p>
                                    )}
                                    {errorMessage && (
                                        <p className="text-red-400 text-sm font-medium">
                                            {errorMessage}
                                        </p>
                                    )}

                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className={`mt-8 flex h-14 w-full items-center justify-center space-x-3 rounded-full px-10 font-bold text-white text-base transition-all duration-300 shadow-lg ${isSubmitting ? 'opacity-70 cursor-not-allowed' : 'hover:scale-105'
                                            }`}
                                        style={{
                                            background: isSubmitting
                                                ? 'linear-gradient(to right, #07111f, #7fd3ff)'
                                                : 'linear-gradient(to right, #7fd3ff, #f7d774)',
                                            boxShadow: '0 6px 20px rgba(127, 211, 255, 0.3)',
                                        }}
                                    >
                                        <span>{isSubmitting ? t('sendingButton') : t('sendButton')}</span>
                                        {!isSubmitting && (
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" className="h-6 w-6">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 19.5 15-15m0 0H8.25m11.25 0v11.25" />
                                            </svg>
                                        )}
                                    </button>
                                </form>
                            </motion.div>
                        </div>
                    </div>
                </div>
            </motion.section>

            {/* MAP SECTION */}
            <motion.section
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={sectionVariants}
                className="border-t border-white/10 bg-[#0b1527]"
            >
                <div className="mx-auto max-w-7xl px-6 py-20 sm:px-10 lg:px-16">
                    <div className="mb-10 max-w-3xl">

                        <h2 className="mt-4 font-serif text-3xl font-semibold text-white sm:text-4xl">
                            {t('findUsTitle')}
                        </h2>
                    </div>
                    <div className="flex justify-center">
                        <div className="w-full">
                            <div className="rounded-[1.5rem] overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.25)] border border-white/10">
                                <iframe
                                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d8373.151699815016!2d139.7699506391586!3d35.671584263852225!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x60188b87bbf5f52b%3A0xa441fdcd47ccc531!2sSonic%20Tours%20Japan!5e1!3m2!1sen!2slk!4v1784114419990!5m2!1sen!2slk"
                                    width="100%"
                                    height="520"
                                    className="border-0"
                                    allowFullScreen=""
                                    loading="lazy"
                                    referrerPolicy="strict-origin-when-cross-origin"
                                    title="Sonic Tours Japan Location"
                                ></iframe>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.section>
        </div>
    );
};

export default ContactUs;